import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { CheckCircleIcon, XCircleIcon, TrophyIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function Quiz() {
  const { user, updateUser } = useAuth();
  const { quizzes, submitQuizAnswer, isInitialized, verifyAndInitializeQuizzes } = useGame();
  const navigate = useNavigate();
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswerLocked, setIsAnswerLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset state when quizzes change
    setCurrentQuiz(null);
    setCurrentQuestion(0);
    setScore(0);
    setShowResult(false);
    setSelectedAnswer(null);
    setIsAnswerLocked(false);
    setIsLoading(true);

    const initQuiz = async () => {
      try {
        if (isInitialized) {
          // Verify and initialize quizzes if needed
          await verifyAndInitializeQuizzes();
          
          if (quizzes && Array.isArray(quizzes) && quizzes.length > 0) {
            const randomQuiz = quizzes[Math.floor(Math.random() * quizzes.length)];
            setCurrentQuiz(randomQuiz);
          }
        }
      } catch (error) {
        console.error('Error initializing quiz:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initQuiz();
  }, [quizzes, isInitialized, verifyAndInitializeQuizzes]);

  const handleAnswer = async (answer) => {
    if (isAnswerLocked || !currentQuiz || !currentQuiz.questions) return;
    
    setIsAnswerLocked(true);
    setSelectedAnswer(answer);
    
    try {
      const result = await submitQuizAnswer(currentQuiz.id, currentQuiz.questions[currentQuestion].id, answer);
      
      if (result.success) {
        if (result.isCorrect) {
          setScore(score + result.points);
          toast.success('Correct answer!');
        } else {
          toast.error('Wrong answer!');
        }

        setTimeout(() => {
          if (currentQuestion < currentQuiz.questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
            setSelectedAnswer(null);
            setIsAnswerLocked(false);
          } else {
            handleQuizEnd();
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Failed to submit answer. Please try again.');
      setIsAnswerLocked(false);
    }
  };

  const handleQuizEnd = async () => {
    setShowResult(true);
    const earnedCoins = score;
    try {
      await updateUser({
        coins: (user?.coins || 0) + earnedCoins,
        quizzesTaken: (user?.quizzesTaken || 0) + 1,
        totalScore: (user?.totalScore || 0) + score
      });
      toast.success(`Congratulations! You earned ${earnedCoins} coins!`);
    } catch (error) {
      console.error('Error updating user data:', error);
      toast.error('Failed to update score. Please try again.');
    }
  };

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen bg-[#F47B6C] p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47B6C] mx-auto mb-4"></div>
          <p className="text-gray-700">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (!currentQuiz || !currentQuiz.questions || currentQuiz.questions.length === 0) {
    return (
      <div className="min-h-screen bg-[#F47B6C] p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-6 text-center space-y-4">
          <p className="text-gray-700">No quizzes available at the moment.</p>
          <p className="text-gray-500 text-sm">Please try again later or check back soon for new quizzes!</p>
          <button
            onClick={() => navigate('/home')}
            className="mt-4 px-6 py-2 bg-[#F47B6C] text-white rounded-full hover:bg-[#F47B6C]/90 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (showResult) {
    return (
      <div className="min-h-screen bg-[#f56756] p-6">
        <div className="bg-[#b34e42] rounded-2xl shadow-lg p-6 text-center space-y-6">
          <div className="flex justify-center">
            <div className="bg-[#402e2c] rounded-full p-4">
              <TrophyIcon className="h-12 w-12 text-[#f56756]" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white">
            Quiz Completed!
          </h2>
          
          <div className="space-y-4">
            <p className="text-xl text-[#402e2c]">
              Your Score: {score} out of {currentQuiz.questions.length * 10}
            </p>
            <p className="text-gray-400">
              You earned {score} coins!
            </p>
          </div>

          <div className="space-y-4 pt-4">
            <button
              onClick={() => {
                setCurrentQuestion(0);
                setScore(0);
                setShowResult(false);
                setSelectedAnswer(null);
                setIsAnswerLocked(false);
                if (quizzes && quizzes.length > 0) {
                  const randomQuiz = quizzes[Math.floor(Math.random() * quizzes.length)];
                  setCurrentQuiz(randomQuiz);
                }
              }}
              className="w-full bg-gradient-to-r from-[#402e2c] to-[#402e2c]/80 text-white rounded-full py-3.5 text-base font-medium"
            >
              Play Again
            </button>
            
            <button
              onClick={() => navigate('/home')}
              className="w-full border border-[#402e2c] text-[#402e2c] rounded-full py-3.5 text-base font-medium bg-[#b34e42]"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestionData = currentQuiz.questions[currentQuestion];

  return (
    <div className="min-h-screen bg-[#f56756] p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate('/home')}
          className="p-2 text-white hover:text-[#402e2c]"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="text-[#402e2c] font-medium">
          Score: {score}
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-[#b34e42] rounded-2xl shadow-lg p-6 space-y-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">
              Question {currentQuestion + 1}/{currentQuiz.questions.length}
            </h2>
            <span className="text-sm text-gray-400">
              {currentQuiz.title}
            </span>
          </div>

          <p className="text-lg text-white">
            {currentQuestionData.question}
          </p>
        </div>

        <div className="space-y-3">
          {currentQuestionData.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(option)}
              disabled={isAnswerLocked}
              className={`w-full p-4 rounded-xl text-left transition-all ${
                selectedAnswer === null
                  ? 'bg-[#f56756] hover:bg-[#b34e42] text-white'
                  : selectedAnswer === option
                  ? option === currentQuestionData.correctAnswer
                    ? 'bg-green-500/20 border border-green-500 text-green-500'
                    : 'bg-red-500/20 border border-red-500 text-red-500'
                  : option === currentQuestionData.correctAnswer && selectedAnswer !== null
                  ? 'bg-green-500/20 border border-green-500 text-green-500'
                  : 'bg-[#f56756] text-white opacity-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{option}</span>
                {selectedAnswer !== null && (
                  option === currentQuestionData.correctAnswer ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : selectedAnswer === option ? (
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                  ) : null
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Quiz; 